import { unwrap } from "@sagot/api-client";
import { ZOHO_INTEGRATION_KEY } from "@sagot/constants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { wsKeys } from "../shared";
import type { ZohoSync, ZohoSyncBody } from "./zoho-sync-model";

const STALE_MS = 15_000;

/** Under the integrations key, so one invalidation of `wsKeys.integrations` refreshes it with the list. */
export function zohoSyncKey(wsId: string) {
  return [...wsKeys.integrations(wsId), ZOHO_INTEGRATION_KEY, "contacts-sync"] as const;
}

/** `GET /v1/integrations/zoho/contacts-sync` (F22): 404 while the platform flag is off for the workspace. Only asked once Zoho is linked. */
export function useZohoSync(enabled: boolean) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: zohoSyncKey(wsId),
    queryFn: () => unwrap(api.GET("/v1/integrations/zoho/contacts-sync")),
    enabled,
    retry: false,
    staleTime: STALE_MS,
  });
}

export function useZohoSyncMutations() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: (body: ZohoSyncBody) => unwrap(api.PUT("/v1/integrations/zoho/contacts-sync", { body })),
    onSuccess: (saved: ZohoSync) => { client.setQueryData(zohoSyncKey(wsId), saved); },
  });
  const run = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/integrations/zoho/contacts-sync/run")),
    onSettled: () => client.invalidateQueries({ queryKey: zohoSyncKey(wsId) }),
  });
  return { save, run };
}
